import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiEnvelopeLight, PiXLight, PiUserLight, PiBuildingLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import { listClients } from '../../store/actions/ClientActions';
import DatePicker from 'react-datepicker';
import clsx from 'clsx';
import 'react-datepicker/dist/react-datepicker.css';

interface SendOnboardingEmailsModalProps {
  selectedClients: string[];
  onClose: () => void;
  onSend: (clientIds: string[], customMessage: string, scheduleDate?: Date) => void;
}

const SendOnboardingEmailsModal: React.FC<SendOnboardingEmailsModalProps> = ({
  selectedClients: initialSelectedClients,
  onClose,
  onSend,
}) => {
  const { t } = useTranslation();
  const [customMessage, setCustomMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(initialSelectedClients);
  const [selectedClientsData, setSelectedClientsData] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  const suggestedTemplate = t(
    'Dear {ClientName},\n\nWe have invited you to collaborate on the PONS platform.\n\nLet us know if you have any questions.\n\nBest regards,\n[Your Name]\n[Firm Name]'
  );

  const convertClientForDisplay = (client: any) => {
    const clientId = String(client.id || client.uuid);
    return {
      ...client,
      id: client.id,
      uuid: client.uuid,
      ClientID: clientId,
      Name: client.name || `Client ${clientId.substring(0, 8)}`,
      Email: client.contact_email || '',
      ClientType: client.type_id ? 'Business' : 'Individual',
    };
  };

  useEffect(() => {
    setCustomMessage(suggestedTemplate);
  }, [suggestedTemplate]);

  useEffect(() => {
    if (initialSelectedClients.length > 0) {
      listClients(1, 100)
        .then((response) => {
          try {
            if (!response || !Array.isArray(response.clients)) {
              console.warn('Invalid response format, skipping client load');
              setSelectedClientsData([]);
              setSelectedClientIds([]);
              return;
            }

            const initialClientSet = new Set(
              initialSelectedClients.map((id) => String(id).toLowerCase())
            );

            // Load selected clients
            const filtered = response.clients
              .filter((c: any) => {
                const clientId = String(c.id || c.uuid).toLowerCase();
                return initialClientSet.has(clientId);
              })
              .map(convertClientForDisplay);

            setSelectedClientsData(filtered);
            setSelectedClientIds(filtered.map((c: any) => String(c.ClientID)));
          } catch (error: any) {
            console.error('Failed to process selected clients:', error?.message);
            setSelectedClientsData([]);
            setSelectedClientIds([]);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch selected clients:', err?.message);
          setSelectedClientsData([]);
          setSelectedClientIds([]);
        });
    }
  }, [initialSelectedClients]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      listClients(1, 100)
        .then((response) => {
          try {
            if (!response || !Array.isArray(response.clients)) {
              console.warn('Invalid response format for search, returning empty');
              setSearchResults([]);
              return;
            }
            // Only show non-onboarded clients that match search and aren't already selected
            const filteredClients = response.clients
              .filter((client: any) =>
                !client.is_onboarded && // Only non-onboarded clients
                (client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                 client.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())) &&
                !selectedClientIds.includes(String(client.id)) &&
                !selectedClientIds.includes(client.uuid)
              )
              .map(convertClientForDisplay);
            setSearchResults(filteredClients);
          } catch (error: any) {
            console.error('Failed to process search results:', error?.message);
            setSearchResults([]);
          }
        })
        .catch((err) => {
          console.error('Failed to search clients:', err?.message);
          setSearchResults([]);
        });
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, selectedClientIds]);

  const handleToggleClient = (clientId: string) => {
    if (selectedClientIds.includes(clientId)) {
      setSelectedClientIds((prev) => prev.filter((id) => id !== clientId));
      setSelectedClientsData((prev) => prev.filter((c) => c.ClientID !== clientId));
    } else {
      const client = searchResults.find((c) => String(c.ClientID) === clientId || c.ClientID === clientId);
      if (client) {
        setSelectedClientIds((prev) => [...prev, clientId]);
        setSelectedClientsData((prev) => [...prev, client]);
      }
    }
  };

  const handleSend = () => {
    if (selectedClientIds.length === 0) {
      alert(t('Please select at least one client.'));
      return;
    }
    const enrichedMessage = selectedClientIds.map((clientId) => {
      const client = selectedClientsData.find((c) => c.ClientID === clientId) || {
        Name: 'Unknown',
        Email: 'unknown@example.com',
        ClientType: 'Unknown',
        ExternalCaseCount: 0,
      };
      return customMessage
        .replace('{ClientName}', client.Name)
        .replace('{ClientEmail}', client.Email)
        .replace('{ClientType}', client.ClientType)
        .replace('{ExternalCaseCount}', client.ExternalCaseCount.toString());
    }).join('\n\n---\n\n');
    onSend(selectedClientIds, enrichedMessage, scheduleDate as Date);
    onClose();
  };

  const visibleClients = showAllRecipients ? selectedClientIds : selectedClientIds.slice(0, 3);
  const hiddenClients = selectedClientIds.slice(3);

  const placeholderOptions = [
    { label: '{ClientName}', value: '{ClientName}' },
    { label: '{ClientEmail}', value: '{ClientEmail}' },
    { label: '{ClientType}', value: '{ClientType}' },
    { label: '{ExternalCaseCount}', value: '{ExternalCaseCount}' },
  ];

  const handleAddPlaceholder = (placeholder: string) => {
    setCustomMessage((prev) => prev + placeholder);
  };

  return (
    <div className="w-full flex flex-col min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 flex justify-between items-center px-6 py-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <PiEnvelopeLight className="h-6 w-6 text-primary-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('Send Onboarding Emails')}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <PiXLight className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
        {/* Recipients Section */}
        <div className="relative" ref={searchContainerRef}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('Recipients')}
          </label>

          {/* Selected Recipients Display */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-3 min-h-[56px] flex flex-wrap items-start content-start gap-2">
            {selectedClientIds.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 w-full">
                {t('No recipients selected')}
              </p>
            ) : (
              <>
                {visibleClients.map((clientId) => {
                  const client = selectedClientsData.find((c) => c.ClientID === clientId) || {
                    Name: 'Unknown',
                    Email: '',
                    ClientType: '',
                  };
                  return (
                    <div
                      key={clientId}
                      className="flex items-center gap-2 bg-white dark:bg-gray-700 px-3 py-2 rounded-full border border-gray-200 dark:border-gray-600 text-sm"
                    >
                      {client.ClientType === 'Business' ? (
                        <PiBuildingLight className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      ) : (
                        <PiUserLight className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      )}
                      <span className="text-gray-700 dark:text-gray-200 truncate">
                        {client.Name}
                      </span>
                      <button
                        onClick={() => handleToggleClient(clientId)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ml-1 flex-shrink-0"
                        title={t('Remove')}
                      >
                        <PiXLight className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
                {hiddenClients.length > 0 && !showAllRecipients && (
                  <button
                    onClick={() => setShowAllRecipients(true)}
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    +{hiddenClients.length} {t('more')}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Add More Clients Input */}
          <div className="relative">
            <input
              type="text"
              placeholder={t('Search to add more clients...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-all text-sm"
            />

            {/* Search Results Dropdown */}
            {searchQuery && searchResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                {searchResults.map((client) => (
                  <button
                    key={client.ClientID}
                    onClick={() => {
                      handleToggleClient(client.ClientID);
                      setSearchQuery('');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors text-sm"
                  >
                    {client.ClientType === 'Business' ? (
                      <PiBuildingLight className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    ) : (
                      <PiUserLight className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 dark:text-gray-100 font-medium truncate">
                        {client.Name}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs truncate">
                        {client.Email}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchQuery && searchResults.length === 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 p-3 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('No available clients found')}
              </div>
            )}
          </div>
        </div>

        {/* Message Section */}
        <div>
          <Textarea
            rows={8}
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            id="customMessage"
            label={t("Email Message")}
            h="h-[240px]"
            placeholder={t('Compose your message...')}
          />
          <div className="mt-3 space-y-2">
            <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
              {t('Quick insert placeholders:')}
            </p>
            <div className="flex flex-wrap gap-2">
              {placeholderOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAddPlaceholder(option.value)}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  type="button"
                >
                  + {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Schedule Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('Schedule (Optional)')}
          </label>
          <DatePicker
            selected={scheduleDate}
            onChange={(date: Date) => setScheduleDate(date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText={t('Select a date and time to schedule')}
            className={clsx(
              'w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg',
              'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100',
              'placeholder-gray-500 dark:placeholder-gray-400',
              'focus:outline-none focus:ring-1 focus:ring-primary-500',
              'transition-all text-sm'
            )}
          />
          {scheduleDate && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('Emails will be sent on')} {scheduleDate.toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSend}
          disabled={selectedClientIds.length === 0}
        >
          {scheduleDate ? t('Schedule Emails') : t('Send Emails')}
        </Button>
      </div>
    </div>
  );
};

export default SendOnboardingEmailsModal;
